// src/components/AboutFounder.js
import React from "react";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Stack,
  Divider,
} from "@mui/material";
import founderImg from "./founder.png";
import { Avatar } from "@mui/material";
import "./AboutFounder.css";

export default function AboutFounder() {
  return (
    <Box sx={{ marginTop: "200px", color: "#d0d0d0", py: { xs: 6, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Headline */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 800, color: "#fff", mb: 1 }}
          >
            Building Systems that Improve Outcomes
          </Typography>
          <Typography variant="subtitle1" sx={{ color: "#9aa0a6", fontStyle: "italic" }}>
            From classrooms to codebases, I build systems that turn effort into fluency.
          </Typography>
        </Box>

        {/* Story Card */}
        <Card
          elevation={0}
          sx={{
            bgcolor: "#201f1ff1",
            border: "1px solid #333",
            borderRadius: 3,
            boxShadow: "0 8px 24px rgba(0,255,180,0.06)",
            mb: 4,
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={3}
              alignItems={{ xs: "center", md: "flex-start" }}
            >
              {/* Photo */}
              <img
                src={founderImg}
                alt="Steven Kurowski"
                className="founder-avatar-mobile"
                sx={{ width: { xs: 210, sm: 230 }, height: { xs: 210, sm: 230 }, border: "2px solid #2e2e2e" }}
              />
              <Avatar
                src={founderImg}
                alt="Steven Kurowski"
                className="founder-avatar-desktop"
                sx={{
                  width: { xs: 210, sm: 230 },
                  height: { xs: 210, sm: 230 },
                  border: "2px solid #2e2e2e",
                }}
              />
              {/* Text */}
              <Stack spacing={2}>
                <Typography>
                  I’m <strong>Steven Kurowski</strong> — a linguist, educator, and self-taught
                  full-stack developer. After twenty years teaching English across South Korea
                  and Oman, I saw learners who could read and write English but couldn’t <em>speak</em> it
                  with confidence. Recalling my experience in theatre training, I realized if learners
                  could feel what their instructor's voice was doing, they could speak with greater fluency
                  more quickly.  That insight began my journey as a systems builder.
                </Typography>
                <Typography>
                  In every environment — universities, tech startups, and classrooms — I found
                  that clarity and structure could solve human problems. I created scheduling
                  dashboards that reclaimed 33% of academic planning, analysis, and delivery time, quality-assurance
                  sheets that eliminated grading errors, and workflow trackers that helped autistic
                  employees measure and celebrate their productivity. Each system turned frustration
                  into flow.
                </Typography>
                <Typography>
                  Those experiences converged in <strong>The iSpeakWell Project</strong>, a platform
                  that transforms passive language study into active, measurable speaking practice.
                  It integrates AI phoneme analysis, real-time avatar feedback, a discussion tracker,
                  and even a tactile “VoiceBot”, emerging from my initial insight, that lets users feel
                  the resonance of their own voice through their fingertips.
                </Typography>
                <Typography>
                  My goal is simple: empower people to open their mouths, speak boldly, and measure
                  their own progress. Whether through data, design, or dialogue, I build tools that
                  make human communication visible, repeatable, and achievable.
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>


        {/* Mission & Vision */}
        <Box sx={{ marginTop: "200px", textAlign: "center", mb: 2 }}>
          <Typography variant="h4" component="h2" sx={{ color: "#fff", fontWeight: 700 }}>
            Mission &amp; Vision
          </Typography>
        </Box>

        <Card
          elevation={0}
          sx={{
            bgcolor: "#201f1ff1",
            border: "1px solid #333",
            borderRadius: 3,
            mb: 4,
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            <Stack spacing={2.25}>
              <Box>
                <Typography variant="h6" sx={{ color: "#53e2c2", fontWeight: 700, mb: 0.5 }}>
                  Our Mission
                </Typography>
                <Typography>
                  The mission of <strong>The iSpeakWell Project</strong> is to help every English
                  learner speak with confidence, clarity, and pride. We design tools that make
                  fluency measurable and attainable for everyone — regardless of accent, background,
                  or learning style.
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" sx={{ color: "#53e2c2", fontWeight: 700, mb: 0.5 }}>
                  Our Vision
                </Typography>
                <Typography>
                  We envision a world where learners don’t just study language — they <em>live</em> it.
                  Where AI becomes a coach, not a judge; where technology amplifies human expression
                  instead of replacing it. iSpeakWell makes language learning creative, inclusive,
                  and social.
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" sx={{ color: "#53e2c2", fontWeight: 700, mb: 0.5 }}>
                  Our Values
                </Typography>
                <Stack component="ul" spacing={1} sx={{ pl: 2, m: 0 }}>
                  <li>
                    <Typography>
                      <strong>Human-Centered Design:</strong> Every feature begins with empathy and ends with empowerment.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      <strong>Transparency:</strong> Users own their data, track progress, and control their journey.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      <strong>Accessibility:</strong> We embrace all learners — neurodiverse, multilingual, differently-abled.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      <strong>Creativity:</strong> Speech is an art form — expressive, adaptive, and personal.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      <strong>Integrity:</strong> Ethical AI, open communication, responsible growth.
                    </Typography>
                  </li>
                </Stack>
              </Box>

              <Divider sx={{ borderColor: "#222", my: 1.5 }} />

              <Box>
                <Typography variant="h6" sx={{ color: "#53e2c2", fontWeight: 700, mb: 0.5 }}>
                  Next Development Goals
                </Typography>
                <Stack component="ul" spacing={1} sx={{ pl: 2, m: 0 }}>
                  <li><Typography>Launch beta testing with 25 learners and 5 ESL coaches in Alberta.</Typography></li>
                  <li><Typography>Integrate the <strong>VoiceBot</strong> tactile feedback system.</Typography></li>
                  <li><Typography>Release the <strong>iSpeakWell Project</strong>.</Typography></li>
                  <li><Typography>Partner with schools, nonprofits, and creators to scale globally.</Typography></li>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Timeline */}
        <Box sx={{ marginTop: "200px", textAlign: "center", mb: 2 }}>
          <Typography variant="h4" component="h2" sx={{ color: "#fff", fontWeight: 700 }}>
            Timeline of Innovation
          </Typography>
        </Box>

        <Grid
          container
          spacing={2}
          sx={{ mb: 4, justifyContent: { xs: "center", md: "space-between" } }}
        >
          {[
            { emoji: "🎓", title: "Teaching & Theatre", text: "2000–2013 • ESL instruction in Korea using theatre-based fluency methods." },
            { emoji: "🧠", title: "Automation & Data Systems", text: "2013–2018 • Dashboards & QA systems for universities and tech firms." },
            { emoji: "📻", title: "VoiceBot Prototype", text: "2019–2021 • Tactile device translating speech frequencies into vibration." },
            { emoji: "🌐", title: "iSpeakWell Platform", text: "2021–2024 • AI-driven fluency web app with avatar feedback." },
            { emoji: "🚀", title: "Pilot & Scale", text: "2025+ • Beta with learners and partners in Alberta." },
          ].map((item) => (
            <Grid key={item.title} item xs={12} sm={6} md={2.4} /* 5-up layout */>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  bgcolor: "#201f1ff1",
                  border: "1px dashed #2b2b2b",
                  borderRadius: 2,
                  textAlign: "center",
                }}
              >
                <CardContent>
                  <Box sx={{ fontSize: 28, mb: 1 }}>{item.emoji}</Box>
                  <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 700 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#9aa0a6", mt: 0.5 }}>
                    {item.text}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* CTA */}
        <Box sx={{ marginTop: "140px", textAlign: "center" }}>
          <Button
            variant="contained"
            size="large"
            href="https://ispeakwell.ca/contactus"
            target="_blank"
            rel="noreferrer"
            sx={{
              fontWeight: 800,
              borderRadius: 2,
              textTransform: "none",
              bgcolor: "#00c896",
              color: "#000",
              "&:hover": { bgcolor: "#00ffb0" },
              boxShadow: "0 8px 18px rgba(0,255,180,0.15)",
            }}
          >
            Connect with Steven
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
